/**
 * LYNKSTAR API Service
 * Handles all HTTP API calls to the backend
 */

const API_URL = import.meta.env.VITE_API_URL || 'https://api.lynkstar.app/api/v1'

// Types
export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed'

export interface User {
  id: string
  username: string
  avatar?: string
  avatar_url?: string
  display_name?: string
  publicKey?: string
  isOnline?: boolean
  status_text?: string
}

export interface Message {
  id: string
  senderId: string
  text: string
  timestamp: string
  status: MessageStatus
  ciphertext?: string
  reply_to?: string
  reactions?: Record<string, string[]>  // emoji -> user_ids
  edited?: boolean
  deleted?: boolean
}

export interface Conversation {
  recipientId: string
  recipientName?: string
  recipientAvatar?: string
  isOnline: boolean
  lastMessage?: Message
  unreadCount: number
}

export interface PreKeyBundle {
  identityKey: string
  signedPreKey: string
  signedPreKeySignature: string
  signedPreKeyId: number
  oneTimePreKey?: string
  oneTimePreKeyId?: number
}

export interface AuthResponse {
  token: string
  user: User
}

export interface Group {
  id: string
  name: string
  avatar_url?: string
  description?: string
  creator_id: string
  created_at: string
  member_count: number
  max_members?: number
  role?: string
  my_role?: string
}

export interface GroupInvite {
  id: string
  code: string
  url: string
  expires_at: string
}

export interface GroupPin {
  id: number
  message_id: string
  pinned_by: string
  pinned_at: string
  ciphertext: string
  senderId: string
  messageTimestamp: string
  senderName: string
  pinnedByName: string
}

export interface GroupMember {
  id: string
  username: string
  avatar_url?: string
  role: string
  joined_at: string
}

export interface GroupMessage {
  id: string
  senderId: string
  senderName?: string
  ciphertext: string
  timestamp: string
  text?: string
}

export interface ApiError extends Error {
  status?: number
  code?: string
}

// Helper function for API requests
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('lynkstar_token')

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const error: ApiError = new Error('API request failed')
    error.status = response.status

    try {
      const data = await response.json()
      error.message = data.message || data.error || 'Request failed'
      error.code = data.code
    } catch {
      error.message = `HTTP ${response.status}: ${response.statusText}`
    }

    throw error
  }

  // Handle empty responses
  const text = await response.text()
  if (!text) return {} as T

  return JSON.parse(text) as T
}

// API methods
export const api = {
  // Authentication

  async getChallenge(username: string): Promise<{ user_id: string; challenge: string }> {
    return request<{ success: boolean; user_id: string; challenge: string }>('/auth/challenge', {
      method: 'POST',
      body: JSON.stringify({ username }),
    })
  },

  async login(username: string, challenge?: string, signature?: string): Promise<AuthResponse> {
    if (!challenge) {
      const challengeResp = await this.getChallenge(username)
      return request<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          username,
          user_id: challengeResp.user_id,
          challenge: challengeResp.challenge,
          signature: signature || 'challenge-only',
        }),
      })
    }

    return request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, challenge, signature: signature || 'challenge-only' }),
    })
  },

  async register(
    username: string,
    keys: {
      identityKey: string
      signedPreKey: { id: number; publicKey: string; signature: string }
      oneTimePreKeys: { id: number; publicKey: string }[]
    }
  ): Promise<AuthResponse> {
    return request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        username,
        identity_key: keys.identityKey,
        signed_pre_key: keys.signedPreKey.publicKey,
        signed_pre_key_sig: keys.signedPreKey.signature,
        one_time_pre_keys: keys.oneTimePreKeys.map(k => k.publicKey),
      }),
    })
  },

  async logout(): Promise<void> {
    try {
      await request('/auth/logout', { method: 'POST' })
    } catch {
      // Ignore logout errors
    }
  },

  // User
  async getCurrentUser(): Promise<User> {
    const data = await request<{ success?: boolean } & User & { identity_key?: string }>('/user/me')
    const user = data as User & { identity_key?: string }
    if (user.identity_key && !user.publicKey) {
      (user as User).publicKey = user.identity_key
    }
    return user as User
  },

  async getUserByUsername(username: string): Promise<User> {
    const user = await request<User & { identity_key?: string }>(`/user/${encodeURIComponent(username)}`)
    if (user.identity_key && !user.publicKey) {
      (user as User).publicKey = user.identity_key
    }
    return user as User
  },

  async searchUsers(query: string): Promise<User[]> {
    const data = await request<{ success?: boolean; users?: (User & { identity_key?: string })[] } | (User & { identity_key?: string })[]>(`/user/search?q=${encodeURIComponent(query)}`)
    const users = Array.isArray(data) ? data : (data as { users?: (User & { identity_key?: string })[] }).users || []
    return users.map(u => ({
      ...u,
      publicKey: u.publicKey || u.identity_key,
    }))
  },

  async updateProfile(data: { display_name?: string; avatar_url?: string; status_text?: string }): Promise<void> {
    await request('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Key status check (for pre-key auto-replenishment)
  async getKeyStatus(): Promise<{ one_time_pre_keys_remaining: number; signed_pre_key_age?: number }> {
    return request<{ one_time_pre_keys_remaining: number; signed_pre_key_age?: number }>('/keys/status')
  },

  // Pre-keys for Signal Protocol
  async uploadPreKeys(
    signedPreKey: { id: number; publicKey: string; signature: string },
    oneTimePreKeys: { id: number; publicKey: string }[]
  ): Promise<void> {
    await request('/keys/upload', {
      method: 'POST',
      body: JSON.stringify({
        signed_pre_key: signedPreKey,
        one_time_pre_keys: oneTimePreKeys,
      }),
    })
  },

  async getPreKeyBundle(userId: string): Promise<PreKeyBundle> {
    let raw: Record<string, unknown>
    try {
      raw = await request<Record<string, unknown>>(`/keys/${encodeURIComponent(userId)}`)
    } catch {
      raw = await request<Record<string, unknown>>(`/keys/bundle?user_id=${encodeURIComponent(userId)}`)
    }
    return {
      identityKey: (raw.identityKey || raw.identity_key || '') as string,
      signedPreKey: (raw.signedPreKey || raw.signed_pre_key || '') as string,
      signedPreKeySignature: (raw.signedPreKeySignature || raw.signed_pre_key_sig || '') as string,
      signedPreKeyId: (raw.signedPreKeyId || raw.signed_pre_key_id || 0) as number,
      oneTimePreKey: (raw.oneTimePreKey || raw.one_time_pre_key) as string | undefined,
      oneTimePreKeyId: (raw.oneTimePreKeyId || raw.one_time_pre_key_id) as number | undefined,
    }
  },

  // Conversations
  async getConversations(): Promise<Conversation[]> {
    const data = await request<{ success?: boolean; conversations?: Conversation[] } | Conversation[]>('/conversations')
    if (Array.isArray(data)) return data
    return (data as { conversations?: Conversation[] }).conversations || []
  },

  async deleteConversation(recipientId: string): Promise<void> {
    await request(`/conversations/${encodeURIComponent(recipientId)}`, {
      method: 'DELETE',
    })
  },

  // Messages
  async getMessages(recipientId: string, limit = 50, before?: string): Promise<Message[]> {
    const params = new URLSearchParams({ limit: limit.toString() })
    if (before) params.append('before', before)

    const data = await request<{ success?: boolean; messages?: Message[] } | Message[]>(
      `/messages/${encodeURIComponent(recipientId)}?${params}`
    )
    if (Array.isArray(data)) return data
    return (data as { messages?: Message[] }).messages || []
  },

  async sendMessage(recipientId: string, ciphertext: string, replyTo?: string): Promise<Message> {
    return request<Message>(`/messages/${encodeURIComponent(recipientId)}`, {
      method: 'POST',
      body: JSON.stringify({ ciphertext, reply_to: replyTo }),
    })
  },

  async editMessage(messageId: string, ciphertext: string): Promise<void> {
    await request(`/messages/${encodeURIComponent(messageId)}`, {
      method: 'PUT',
      body: JSON.stringify({ ciphertext }),
    })
  },

  async deleteMessage(messageId: string): Promise<void> {
    await request(`/messages/${encodeURIComponent(messageId)}`, {
      method: 'DELETE',
    })
  },

  async addReaction(messageId: string, emoji: string): Promise<void> {
    await request(`/messages/${encodeURIComponent(messageId)}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    })
  },

  async removeReaction(messageId: string, emoji: string): Promise<void> {
    await request(`/messages/${encodeURIComponent(messageId)}/reactions`, {
      method: 'DELETE',
      body: JSON.stringify({ emoji }),
    })
  },

  async markAsRead(recipientId: string, messageIds: string[]): Promise<void> {
    await request(`/messages/${encodeURIComponent(recipientId)}/read`, {
      method: 'POST',
      body: JSON.stringify({ message_ids: messageIds }),
    })
  },

  // File upload
  async uploadFile(file: File, recipientId: string): Promise<{ url: string; id: string }> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('recipient_id', recipientId)

    const token = localStorage.getItem('lynkstar_token')
    const response = await fetch(`${API_URL}/media/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    })

    if (!response.ok) {
      throw new Error('File upload failed')
    }

    return response.json()
  },

  // Avatar upload
  async uploadAvatar(file: File): Promise<{ url: string; id: string }> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', 'avatar')

    const token = localStorage.getItem('lynkstar_token')
    const response = await fetch(`${API_URL}/media/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    })

    if (!response.ok) {
      throw new Error('Avatar upload failed')
    }

    return response.json()
  },

  // Call signaling
  async initiateCall(recipientId: string, callType: 'audio' | 'video'): Promise<{ call_id: string }> {
    return request<{ call_id: string }>('/calls/initiate', {
      method: 'POST',
      body: JSON.stringify({ recipient_id: recipientId, call_type: callType }),
    })
  },

  async answerCall(callId: string): Promise<void> {
    await request('/calls/answer', {
      method: 'POST',
      body: JSON.stringify({ call_id: callId }),
    })
  },

  async hangupCall(callId: string): Promise<void> {
    await request('/calls/hangup', {
      method: 'POST',
      body: JSON.stringify({ call_id: callId }),
    })
  },

  // Block/unblock users
  async blockUser(userId: string): Promise<void> {
    await request(`/user/${encodeURIComponent(userId)}/block`, {
      method: 'POST',
    })
  },

  async unblockUser(userId: string): Promise<void> {
    await request(`/user/${encodeURIComponent(userId)}/unblock`, {
      method: 'POST',
    })
  },

  async getBlockedUsers(): Promise<User[]> {
    const data = await request<{ success?: boolean; users?: User[] } | User[]>('/user/blocked')
    if (Array.isArray(data)) return data
    return (data as { users?: User[] }).users || []
  },

  // ============================================
  // Group API
  // ============================================

  async createGroup(name: string, members?: string[], avatarUrl?: string): Promise<Group> {
    return request<Group>('/groups', {
      method: 'POST',
      body: JSON.stringify({ name, members, avatar_url: avatarUrl }),
    })
  },

  async getGroups(): Promise<Group[]> {
    const data = await request<{ success?: boolean; groups?: Group[] } | Group[]>('/groups')
    if (Array.isArray(data)) return data
    return (data as { groups?: Group[] }).groups || []
  },

  async getGroup(groupId: string): Promise<Group> {
    return request<Group>(`/groups/${encodeURIComponent(groupId)}`)
  },

  async updateGroup(groupId: string, data: { name?: string; avatar_url?: string; description?: string }): Promise<void> {
    await request(`/groups/${encodeURIComponent(groupId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async deleteGroup(groupId: string): Promise<void> {
    await request(`/groups/${encodeURIComponent(groupId)}`, {
      method: 'DELETE',
    })
  },

  async getGroupMembers(groupId: string): Promise<GroupMember[]> {
    const data = await request<{ success?: boolean; members?: GroupMember[] } | GroupMember[]>(`/groups/${encodeURIComponent(groupId)}/members`)
    if (Array.isArray(data)) return data
    return (data as { members?: GroupMember[] }).members || []
  },

  async addGroupMembers(groupId: string, memberIds: string[]): Promise<{ added: number }> {
    return request<{ added: number }>(`/groups/${encodeURIComponent(groupId)}/members`, {
      method: 'POST',
      body: JSON.stringify({ members: memberIds }),
    })
  },

  async removeGroupMember(groupId: string, memberId: string): Promise<void> {
    await request(`/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}`, {
      method: 'DELETE',
    })
  },

  async leaveGroup(groupId: string): Promise<{ success: boolean; group_deleted?: boolean }> {
    return request<{ success: boolean; group_deleted?: boolean }>(
      `/groups/${encodeURIComponent(groupId)}/leave`,
      { method: 'POST' }
    )
  },

  async getGroupMessages(groupId: string, limit = 50, before?: string): Promise<GroupMessage[]> {
    let url = `/groups/${encodeURIComponent(groupId)}/messages?limit=${limit}`
    if (before) url += `&before=${encodeURIComponent(before)}`
    const data = await request<{ success?: boolean; messages?: GroupMessage[] } | GroupMessage[]>(url)
    if (Array.isArray(data)) return data
    return (data as { messages?: GroupMessage[] }).messages || []
  },

  async sendGroupMessage(groupId: string, ciphertext: string): Promise<GroupMessage> {
    return request<GroupMessage>(`/groups/${encodeURIComponent(groupId)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ ciphertext }),
    })
  },

  async updateMemberRole(groupId: string, userId: string, role: 'admin' | 'member'): Promise<void> {
    await request(`/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    })
  },

  async createGroupInvite(groupId: string): Promise<GroupInvite> {
    const data = await request<{ success: boolean; invite: GroupInvite }>(`/groups/${encodeURIComponent(groupId)}/invite`, {
      method: 'POST',
    })
    return (data as any).invite
  },

  async joinByInvite(code: string): Promise<Group> {
    const data = await request<{ success: boolean; group: Group }>(`/groups/join/${encodeURIComponent(code)}`, {
      method: 'POST',
    })
    return (data as any).group
  },

  async pinGroupMessage(groupId: string, messageId: string): Promise<{ action: string }> {
    return request<{ action: string }>(`/groups/${encodeURIComponent(groupId)}/pin`, {
      method: 'POST',
      body: JSON.stringify({ message_id: messageId }),
    })
  },

  async getGroupPins(groupId: string): Promise<GroupPin[]> {
    const data = await request<{ success?: boolean; pins?: GroupPin[] }>(`/groups/${encodeURIComponent(groupId)}/pins`)
    return (data as { pins?: GroupPin[] }).pins || []
  },

  // ============================================
  // Passkey / WebAuthn API
  // ============================================

  async getPasskeyRegisterOptions(): Promise<{
    options: PublicKeyCredentialCreationOptions & { challenge: string }
  }> {
    return request<{ options: any }>('/auth/passkey/register-options', {
      method: 'POST',
    })
  },

  async verifyPasskeyRegister(credential: {
    id: string
    rawId: string
    type: string
    response: {
      clientDataJSON: string
      attestationObject: string
      transports?: string[]
    }
    device_name?: string
  }): Promise<{ success: boolean; passkey: { id: string; credential_id: string; device_name: string | null; created_at: string } }> {
    return request('/auth/passkey/register-verify', {
      method: 'POST',
      body: JSON.stringify(credential),
    })
  },

  async getPasskeyLoginOptions(username?: string): Promise<{
    options: { challenge: string; rpId: string; allowCredentials: any[]; userVerification: string; timeout: number }
  }> {
    return request('/auth/passkey/login-options', {
      method: 'POST',
      body: JSON.stringify({ username }),
    })
  },

  async verifyPasskeyLogin(credential: {
    id: string
    rawId: string
    type: string
    response: {
      clientDataJSON: string
      authenticatorData: string
      signature: string
      userHandle?: string
    }
  }): Promise<AuthResponse> {
    return request<AuthResponse>('/auth/passkey/login-verify', {
      method: 'POST',
      body: JSON.stringify(credential),
    })
  },

  async listPasskeys(): Promise<{
    id: string
    credential_id: string
    device_name: string | null
    created_at: string
    last_used_at: string | null
  }[]> {
    const data = await request<{ success?: boolean; passkeys?: any[] }>('/auth/passkey/list')
    return (data as any).passkeys || []
  },

  async deletePasskey(passkeyId: string): Promise<void> {
    await request(`/auth/passkey/${encodeURIComponent(passkeyId)}`, {
      method: 'DELETE',
    })
  },
}

export default api
